import Component from '@glimmer/component';
import { service } from '@ember/service';
import {
  HdsCardContainer,
  HdsIcon,
  HdsTextDisplay,
  HdsTextBody,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { Flex } from 'core/components';

/**
 *
 * `ServicePrincipalWorkloadIdentityProviders::Cards` renders the
 *  Project Level Service Principal Workload Identity Provider cards.
 *  It renders provider cards for a given provider specified in providerCardsTranslations and will include a providers HDS flight icon, title,
 *  description, and button that takes users to the workload identity provider documentation.
 *  The last card rendered will provide users with a link to request a provider not supported.
 *
 *
 * ```
 * <ServicePrincipal::WorkloadIdentityProviders::Cards />
 * ```
 *
 * @class ServicePrincipalWorkloadIdentityProviders::Cards
 *
 */
export default class ServicePrincipalWorkloadIdentityProvidersCards extends Component {
  @service intl;

  get providerCardsTranslations() {
    // Order of cards matter - gcp represents google cloud provider
    let providers = ['aws', 'azure', 'github', 'gitlab', 'gcp', 'openid'];
    return providers.map((provider) => {
      let container = {};

      container['logo'] = `${provider}-color`;
      container['title'] = this.intl.t(
        `components.page.access-control.service-principals.details.workload-identity-providers.cards.${provider}.title`
      );
      container['description'] = this.intl.t(
        `components.page.access-control.service-principals.details.workload-identity-providers.cards.${provider}.description`
      );
      container['viewDocs'] = this.intl.t(
        `components.page.access-control.service-principals.details.workload-identity-providers.cards.button.label.view-docs`
      );
      container['href'] = this.intl.t(
        `components.page.access-control.service-principals.details.workload-identity-providers.cards.${provider}.href`
      ); // TODO update translations href's https://hashicorp.atlassian.net/browse/HCPIE-1456

      return container;
    });
  }
  <template>
    {{#each this.providerCardsTranslations as |provider|}}
      {{#if provider.title}}
        <HdsCardContainer
          @hasBorder={{true}}
          class="workloadIdentityProviderCards__card"
          data-test-service-principal-wip-card-item
        >
          <Flex>
            <div
              class="workloadIdentityProviderCards__logo"
              data-test-service-principal-wip-card-item-logo
            >
              <HdsIcon
                @name={{provider.logo}}
                @size="24"
                @stretched={{true}}
                @isInline={{true}}
              />
            </div>
            <div class="workloadIdentityProviderCards__copy">
              <HdsTextDisplay
                @tag="h4"
                @size="200"
                data-test-service-principal-wip-card-item-title
              >
                {{provider.title}}
              </HdsTextDisplay>
              <HdsTextBody
                @tag="p"
                @size="100"
                @color="highlight-high-contrast"
                data-test-service-principal-wip-card-item-description
              >
                {{provider.description}}
              </HdsTextBody>
            </div>
            <div class="workloadIdentityProviderCards__providerDocsButton">
              <HdsButton
                @text={{provider.viewDocs}}
                @color="secondary"
                @icon="learn-link"
                @iconPosition="trailing"
                @href={{provider.href}}
                data-test-service-principal-wip-card-item-button
              />
            </div>
          </Flex>
        </HdsCardContainer>
        {{! Request a Provider card }}
      {{else}}
        <HdsCardContainer
          @hasBorder={{true}}
          class="workloadIdentityProviderCards__otherCard"
          data-test-service-principal-wip-card-item
        >
          <Flex>
            <div class="workloadIdentityProviderCards__copy">
              <HdsTextBody
                @tag="p"
                @size="100"
                @color="highlight-high-contrast"
                data-test-service-principal-wip-card-item-description
              >
                {{provider.description}}
              </HdsTextBody>
            </div>
          </Flex>
        </HdsCardContainer>
      {{/if}}
    {{/each}}
  </template>
}
